import React from "react";

import { SpecificRange, type TimeRange } from "../../app/state/view/timeline/TimeRange";
import type { TimeSeriesChartState } from "../TimeSeriesChart/state";
import type { TimeSeries } from "../TimeSeriesChart/time-series";

import type { Spec } from "./spec";
import { StatChartWidget } from "./stat-chart";

const LazyTimeSeriesChartWidget = React.lazy(async () => {
	const { TimeSeriesChartWidget } = await import("./time-series-chart");
	return { default: TimeSeriesChartWidget };
});

const LazyBarChartWidget = React.lazy(async () => {
	const { BarChartWidget } = await import("./bar-chart");
	return { default: BarChartWidget };
});

const LazyGaugeChartWidget = React.lazy(async () => {
	const { GaugeChartWidget } = await import("./gauge-chart");
	return { default: GaugeChartWidget };
});

export interface ChartProps {
	spec: Spec;
	state?: TimeSeriesChartState;
	timeSeries: ReadonlyArray<TimeSeries>;
	timeRange: TimeRange;
	onTimeRangeChange?: (timeRange: SpecificRange) => void;
	onShowAll: VoidFunction;

	/**
	 * Only applied when `spec.kind` is `TimeSeriesChart` or `BarChart`.
	 * Ignored for `GaugeChart` and `StatChart`.
	 */
	canvasHeight?: number;

	/**
	 * The React.Suspense's fallback
	 */
	fallback: React.ReactNode;
}

export const Chart: React.FC<ChartProps> = ({
	spec,
	state,
	timeSeries,
	timeRange,
	onTimeRangeChange,
	onShowAll,
	canvasHeight,
	fallback,
}) => (
	<React.Suspense fallback={fallback}>
		{spec.kind === "TimeSeriesChart" && (
			<LazyTimeSeriesChartWidget
				canvasHeight={canvasHeight}
				maxVisibleTimeSeries={{ count: 5, onShowAll }}
				onBrush={onTimeRangeChange ? (start, end) => onTimeRangeChange(new SpecificRange(start, end)) : undefined}
				state={state}
				timeRange={timeRange}
				timeSeries={timeSeries}
				{...spec.settings}
			/>
		)}

		{spec.kind === "BarChart" && (
			<LazyBarChartWidget
				canvasHeight={canvasHeight}
				maxVisibleTimeSeries={{ count: 5, onShowAll }}
				onBrush={onTimeRangeChange ? (start, end) => onTimeRangeChange(new SpecificRange(start, end)) : undefined}
				state={state}
				timeRange={timeRange}
				timeSeries={timeSeries}
				{...spec.settings}
			/>
		)}

		{spec.kind === "GaugeChart" && (
			<LazyGaugeChartWidget
				className="h-full"
				minCellSize={120}
				onShowAll={onShowAll}
				timeRange={timeRange}
				timeSeries={timeSeries}
				{...spec.settings}
			/>
		)}

		{spec.kind === "StatChart" && (
			<StatChartWidget
				className="h-full"
				minCellSize={120}
				onShowAll={onShowAll}
				timeRange={timeRange}
				timeSeries={timeSeries}
				{...spec.settings}
			/>
		)}
	</React.Suspense>
);
